/**
 * "Measured on this rig" — what a profile *claims*, beside what it actually
 * does, with a button per figure to adopt the measurement.
 *
 * ## Why this lives on the equipment page
 *
 * The suggestion already existed, on the batch page: brew a second batch and a
 * card appears saying *"your rig measures 65.6 % where the profile says 70 %"*.
 * That card is useful **in context** and it stays. But it is not where anyone
 * goes looking for it. A brewer who wants to know how their system is running
 * opens the page named after their system, and until now that page showed only
 * the nineteen numbers they had typed in — a profile with no idea whether it
 * was true. Feedback that only appears when you happen to be reading a batch is
 * feedback most brewers meet once and never find again.
 *
 * So the same aggregate is rendered here as a standing surface, per profile,
 * from the same `equipmentFeedback` call. Two views of one number: neither can
 * suggest something the other does not.
 *
 * ## Why each figure applies on its own
 *
 * The batch card writes all three fields in one command, which is right for
 * "your rig has drifted, accept the new calibration". A profile page is a
 * different conversation: a brewer may know exactly why their boil-off is high
 * this summer and want that number and not the efficiency one. So there is a
 * button per row, each dispatching one `equipment.update` carrying exactly one
 * field — one thought, one command, one undo.
 *
 * The value written is the **median**, rounded through the same
 * format-then-parse round trip the profile editor's own inputs perform, so
 * pressing "Use measured" and opening the editor show the same number rather
 * than a figure with eleven decimal places behind it. See `bridge/actuals.ts`
 * for why the aggregate is a median and not a mean: one stuck mash must not
 * move a rig's calibration.
 *
 * ## Three figures, not nineteen
 *
 * Only mash efficiency, brewhouse efficiency and boil-off rate are here, because
 * they are the only three of a profile's fields a batch can back-compute. A
 * deadspace or a grain absorption rate is not measured on brew day; inventing a
 * row for it would be a promise the data cannot keep.
 */

import type { EquipmentProfileDoc, WorkspaceState } from '@beerwright/commands';
import { equipmentFeedback } from '@beerwright/commands';
import { useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import type { MeasuredProfileKey } from '@/lib/equipment';
import { whatAdoptingMoves } from '@/lib/equipment';
import { formatPercent } from '@/lib/format';
import type { MeasureKind } from '@/lib/units';
import { formatQuantity, parseQuantity, unitLabel } from '@/lib/units';
import { cn } from '@/lib/utils';
import { useDispatch } from '@/store/use-dispatch';
import { useUnits, useWorkspaceSelector } from '@/store/workspace-context';

/**
 * The three profile fields a batch's measurements can back-compute.
 *
 * Named in `lib/equipment` rather than here because the batch page's card
 * offers the same three, and one of the three means something different from
 * the other two — see {@link whatAdoptingMoves}, which both surfaces read.
 */
type MeasuredKey = MeasuredProfileKey;

/**
 * What one row needs, all of it decided here so the JSX only prints.
 *
 * `measured`, `delta` and `applyValue` move together: they are all `null`
 * exactly when no batch on this rig supported the figure, in which case `note`
 * names the reading that would.
 */
interface MeasuredRow {
  readonly key: MeasuredKey;
  readonly label: string;
  /** The profile's current value, formatted for display. */
  readonly configured: string;
  /** The median of the measured values, formatted, or `null`. */
  readonly measured: string | null;
  /** `median − configured`, signed and formatted, or `null`. */
  readonly delta: string | null;
  /** True when the rig is running below its profile — the actuals table's warning colour. */
  readonly low: boolean;
  /** "median of 4 batches", or the measurement the figure is waiting on. */
  readonly note: string;
  /** The canonical value "Use measured" writes, rounded as the editor's inputs round. */
  readonly applyValue: number | null;
}

/**
 * What each figure needs before it exists at all, taken from the table in
 * `bridge/actuals.ts`'s header — the same requirements `computeBatchActuals`
 * enforces, said in the brewer's words.
 *
 * Naming the missing reading is the whole value of an empty row: "—" tells a
 * brewer nothing, "needs an OG and a fermenter volume" tells them what to write
 * down next time.
 */
const NEEDS: Readonly<Record<MeasuredKey, string>> = {
  mashEfficiencyPercent: 'Needs a post-boil (or pre-boil) gravity and volume',
  brewhouseEfficiencyPercent: 'Needs an original gravity and a volume into the fermenter',
  boilOffRateLPerHour: 'Needs a pre-boil and a post-boil volume',
};

/** Row order, with the unit each figure is measured in. */
const FIGURES: readonly {
  readonly key: MeasuredKey;
  readonly label: string;
  readonly kind: MeasureKind;
}[] = [
  { key: 'mashEfficiencyPercent', label: 'Mash efficiency', kind: 'percent' },
  { key: 'brewhouseEfficiencyPercent', label: 'Brewhouse efficiency', kind: 'percent' },
  { key: 'boilOffRateLPerHour', label: 'Boil-off rate', kind: 'volumeRate' },
];

/**
 * One field of a profile as an `equipment.update` change set.
 *
 * Written out key by key rather than as a computed property so that the payload
 * is typed as the document field it is — a renamed or retyped field becomes a
 * compile error here rather than a command the reducer rejects at runtime.
 */
function changeFor(key: MeasuredKey, value: number): Partial<EquipmentProfileDoc> {
  switch (key) {
    case 'mashEfficiencyPercent':
      return { mashEfficiencyPercent: value };
    case 'brewhouseEfficiencyPercent':
      return { brewhouseEfficiencyPercent: value };
    case 'boilOffRateLPerHour':
      return { boilOffRateLPerHour: value };
  }
}

/**
 * Efficiencies are unitless percentages; the boil-off rate is a volume per hour
 * and goes through the app's unit helpers like every other volume on this page,
 * so a US brewer reads gal/h and a metric one L/h.
 */
function formatFigure(
  value: number,
  kind: MeasureKind,
  units: Parameters<typeof formatQuantity>[2],
): string {
  if (kind === 'percent') return `${formatPercent(value)} %`;
  return `${formatQuantity(value, kind, units)} ${unitLabel(kind, units)}`;
}

/**
 * The value the profile would be given: the median put through the display
 * round trip the editor's inputs already perform.
 *
 * Without it, adopting a 3.7499999 L/h median would store a number the editor
 * then redisplays as `3.75` — a document that disagrees with every screen
 * showing it. Falling back to the raw median if the round trip fails is
 * deliberate: an unparseable format is a bug in the formatter, not a reason to
 * refuse the brewer their measurement.
 */
function roundLikeInput(
  value: number,
  kind: MeasureKind,
  units: Parameters<typeof formatQuantity>[2],
): number {
  const parsed = parseQuantity(formatQuantity(value, kind, units), kind, units);
  return parsed.ok ? parsed.value : value;
}

/** Signed like the actuals table's delta column, with a real minus sign. */
function formatDelta(
  value: number,
  kind: MeasureKind,
  units: Parameters<typeof formatQuantity>[2],
): string {
  const magnitude = formatFigure(value, kind, units).replace(/^-/, '−');
  return value > 0 ? `+${magnitude}` : magnitude;
}

/** Every row of the table, computed once per feedback change. */
function buildRows(
  feedback: ReturnType<typeof equipmentFeedback>,
  profile: EquipmentProfileDoc,
  units: Parameters<typeof formatQuantity>[2],
): readonly MeasuredRow[] {
  return FIGURES.map(({ key, label, kind }) => {
    const figure = feedback[key];
    const configured = formatFigure(profile[key], kind, units);
    if (figure === null) {
      return {
        key,
        label,
        configured,
        measured: null,
        delta: null,
        low: false,
        note: NEEDS[key],
        applyValue: null,
      };
    }
    return {
      key,
      label,
      configured,
      measured: formatFigure(figure.measuredMedian, kind, units),
      delta: formatDelta(figure.delta, kind, units),
      low: figure.delta < 0,
      note: `median of ${figure.sampleCount} ${figure.sampleCount === 1 ? 'batch' : 'batches'}`,
      applyValue: roundLikeInput(figure.measuredMedian, kind, units),
    };
  });
}

/**
 * The measured half of a profile.
 *
 * Subscribed to the store rather than memoised over props, exactly as the batch
 * page's card is: applying a suggestion must leave the row agreeing with
 * itself, and a batch measured in another tab must reach this page without a
 * reload. The aggregate is cheap despite being called once per profile —
 * `equipmentFeedback` filters on the batch's snapshotted rig before it computes
 * anything, and a batch belongs to exactly one rig.
 */
export function MeasuredFigures({ profile }: { readonly profile: EquipmentProfileDoc }) {
  const units = useUnits();
  const { dispatch } = useDispatch();
  const selector = useCallback(
    (state: WorkspaceState) => equipmentFeedback(state, profile.id),
    [profile.id],
  );
  const feedback = useWorkspaceSelector(selector);
  const rows = useMemo(() => buildRows(feedback, profile, units), [feedback, profile, units]);
  // The rows a batch on this rig actually supported. A figure still waiting on
  // a reading has nothing to say about what it would move.
  const measured = rows.filter((row) => row.measured !== null).map((row) => row.key);

  return (
    <section
      className="flex flex-col gap-1.5 border-t border-border/60 pt-2"
      data-testid={`measured-figures-${profile.id}`}
    >
      <h3 className="text-[11px] font-medium tracking-wide text-muted-foreground uppercase">
        Measured on this rig
      </h3>

      {feedback.batchCount === 0 ? (
        <p className="text-xs text-muted-foreground">
          You have not measured anything on this rig yet. Log a gravity or a volume on a batch
          brewed here and we fill these in.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {rows.map((row) => (
            <FigureRow
              key={row.key}
              row={row}
              profile={profile}
              onApply={(key, value) =>
                dispatch('equipment.update', {
                  profileId: profile.id,
                  changes: changeFor(key, value),
                })
              }
            />
          ))}
        </ul>
      )}

      {measured.length > 0 && (
        <p className="text-xs text-muted-foreground">{whatAdoptingMoves(measured)}</p>
      )}

      {feedback.batchCount > 0 && profile.builtin && (
        <p className="text-xs text-muted-foreground">
          {profile.name} is a built-in profile and cannot be edited. Duplicate it to record your own
          numbers.
        </p>
      )}
    </section>
  );
}

/**
 * One figure: what the profile says, what the rig did, the gap, and — on a
 * profile the brewer owns — the button that closes it.
 *
 * `applyValue` is destructured rather than read off `row` so that the null check
 * narrows it for the click handler: a row with no measurement has no button at
 * all, and the compiler knows it.
 */
function FigureRow({
  row,
  profile,
  onApply,
}: {
  readonly row: MeasuredRow;
  readonly profile: EquipmentProfileDoc;
  readonly onApply: (key: MeasuredKey, value: number) => void;
}) {
  const { applyValue } = row;
  return (
    <li className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[11px]">
      <span className="min-w-36 font-medium">{row.label}</span>
      <span className="text-muted-foreground">
        profile <span className="font-mono tabular-nums">{row.configured}</span>
      </span>
      {row.measured !== null && (
        <span className="text-muted-foreground">
          measured <span className="font-mono tabular-nums text-foreground">{row.measured}</span>
        </span>
      )}
      {row.delta !== null && (
        <span
          className={cn(
            'font-mono tabular-nums',
            row.low ? 'text-warning' : 'text-muted-foreground',
          )}
        >
          {row.delta}
        </span>
      )}
      <span className="text-muted-foreground/80">{row.note}</span>
      {!profile.builtin && applyValue !== null && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto h-6 px-2 text-[11px]"
          aria-label={`Use measured ${row.label.toLowerCase()} for ${profile.name}`}
          onClick={() => onApply(row.key, applyValue)}
        >
          Use measured
        </Button>
      )}
    </li>
  );
}
